import {analyseCondition, ConditionNode} from '../workflow-condition';
import {buildConditionVariables, compileCondition, ConditionVariable} from '../conditions/visual/workflow-condition-model';

/** Maps a workflow identifier to a patient field or exported questionnaire value. */
export interface WorkflowMapping {
  name: string;
  target: {type: 'survey' | 'questionnaire'; id?: string; variable: string; options?: any};
}

/** Describes a selectable source independently of its eventual workflow identifier. */
export interface VariableSource extends ConditionVariable { target: WorkflowMapping['target']; }

/** Returns a chosen mapping and the resolved label for immediate use inside a condition draft. */
export interface VariableSelection { variable: WorkflowMapping; descriptor: ConditionVariable; }

/** Records the step references that need attention before changing a mapping. */
export interface VariableUsage { indices: number[]; unknown: boolean; }

export const PATIENT_FIELDS = ['age', 'ageInMonths', 'birthDate', 'isMale', 'isFemale', 'isDiverse', 'caseNr',
  'customerNr', 'zipCode', 'city', 'street', 'streetNr', 'location', 'status', 'lastName', 'firstName', 'anonymous', 'pseudonymous'];
const RESERVED_NAMES = new Set(('await break case catch class const continue debugger default delete do else enum export extends false ' +
  'finally for function if implements import in instanceof interface let new null package private protected public return static super ' +
  'switch this throw true try typeof var void while with yield undefined NaN Infinity arguments eval').split(' '));

/** Explains invalid or duplicate identifiers without silently rejecting the user's input. */
export function mappingNameIssue(name: string, variables: WorkflowMapping[], original = ''): string {
  if (!name.trim()) { return 'Bitte geben Sie einen Variablennamen ein.'; }
  if (!/^[A-Za-z_$][A-Za-z0-9_$]*$/.test(name) || RESERVED_NAMES.has(name)) {
    return 'Verwenden Sie einen gültigen Namen ohne Leerzeichen, zum Beispiel anzahlKartoffeln.';
  }
  return variables.some(variable => variable.name !== original && variable.name.toLowerCase() === name.toLowerCase()) ?
    'Dieser Variablenname ist bereits vergeben.' : '';
}

/** Suggests a stable readable identifier and adds a suffix only when necessary. */
export function suggestMappingName(source: VariableSource, variables: WorkflowMapping[]): string {
  const words = source.target.variable.split('.').pop().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss').replace(/[^a-zA-Z0-9_$]+/g, ' ').trim().split(/\s+/);
  let base = words.map((word, index) => index ? word[0]?.toUpperCase() + word.slice(1) : word).join('') || 'variable';
  if (!/^[A-Za-z_$]/.test(base) || RESERVED_NAMES.has(base)) { base = 'wert' + base[0].toUpperCase() + base.slice(1); }
  let name = base;
  let suffix = 2;
  while (mappingNameIssue(name, variables)) { name = base + suffix++; }
  return name;
}

/** Resolves source labels and value types using the same catalog as condition chips. */
export function variableSources(questionnaires: any[], customFields: any[], locale: string,
  translate: (text: string) => string): VariableSource[] {
  const targets: WorkflowMapping['target'][] = PATIENT_FIELDS.map(field => ({type: 'survey', id: null, variable: `client.${field}`}));
  customFields.forEach(field => targets.push({type: 'survey', id: null, variable: `client.custom.${field.key}`}));
  questionnaires.forEach(questionnaire => ['inline', 'formula', 'scoring'].forEach(scope => {
    Object.keys(questionnaire.meta?.options?.variables?.[scope] || {}).forEach(name => {
      targets.push({type: 'questionnaire', id: questionnaire.id, variable: `${scope}.${name}`});
    });
  }));
  const mappings = targets.map(target => ({name: target.variable.split('.').pop(), target}));
  const descriptors = buildConditionVariables({variables: mappings}, questionnaires, customFields, locale, translate);
  return descriptors.map((descriptor, index) => {
    const options: any = {format: descriptor.valueType === 'number' ? 'numeric' :
      descriptor.valueType === 'string' ? 'text' : descriptor.valueType};
    if (descriptor.valueType === 'date') { options[descriptor.dateKind || 'fullDate'] = true; }
    return {...descriptor, target: {...targets[index], options}};
  });
}

/** Finds all parsed references and flags expressions whose references cannot be inspected safely. */
export function mappingUsage(workflow: any, name: string): VariableUsage {
  const result: VariableUsage = {indices: [], unknown: false};
  (workflow.stages || []).forEach((stage, index) => {
    const analysis = analyseCondition(stage.expression);
    if (analysis.error) { result.unknown = true; }
    if (analysis.references.includes(name)) { result.indices.push(index); }
  });
  return result;
}

/** Identifies the earliest configured source step without promising that a conditional source will actually run. */
export function sourceStage(workflow: any, target: WorkflowMapping['target']): number {
  if (target.type === 'survey') { return -1; }
  return (workflow.stages || []).findIndex(stage => (stage.questionnaires || []).includes(target.id));
}

/** Changes only actual identifier references, never occurrences inside quoted comparison values. */
export function renameMappingExpressions(workflow: any, oldName: string, newName: string): string[] {
  if (oldName === newName) { return (workflow.stages || []).map(stage => stage.expression); }
  if (mappingUsage(workflow, oldName).unknown) { throw new Error('Unsupported conditions prevent safe variable renaming'); }
  return (workflow.stages || []).map(stage => {
    const analysis = analyseCondition(stage.expression);
    if (!analysis.references.includes(oldName)) { return stage.expression; }
    /** Renames reference nodes while preserving literal types and group operators. */
    const rename = (node: ConditionNode): void => {
      if (node.kind === 'group') { node.children.forEach(rename); return; }
      if (node.variable === oldName) { node.variable = newName; }
    };
    rename(analysis.root);
    return compileCondition(analysis.root);
  });
}
